import React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@cnx-saas/snackbar';
import {
  useGetMe_Query,
  useGetMyTenant_Query,
  UserType,
  useUpdateMyTenant_Mutation,
} from '@cnx-saas/common/generated';

export const LogoUploader: React.FC = () => {
  const { t } = useTranslation();
  const { addAlert } = useSnackbar();

  const { data: getMeData } = useGetMe_Query();
  const { data: tenantData, refetch: refetchTenantData } = useGetMyTenant_Query(
    {
      fetchPolicy: 'network-only',
    },
  );
  const [updateMyTenant] = useUpdateMyTenant_Mutation();

  const logoUrl = tenantData?.getMyTenant?.logoUrl;

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async () => {
        const result = reader.result as string;

        await updateMyTenant({
          variables: {
            details: {
              logoUrl: result,
            },
          },
        });

        await refetchTenantData();

        addAlert({
          type: 'success',
          children: t('account.logo.success', {
            defaultValue: 'Logo updated successfully',
          }),
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemove = async () => {
    await updateMyTenant({
      variables: {
        details: {
          logoUrl: undefined,
        },
      },
    });

    await refetchTenantData();

    addAlert({
      type: 'success',
      children: t('account.logo.success', {
        defaultValue: 'Logo updated successfully',
      }),
    });
  };

  if (getMeData?.getMe?.userType !== UserType.TeamAdmin) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        alignItems: 'center',
      }}
    >
      <Typography variant="h6">{t('account.logo.upload')}</Typography>
      {logoUrl ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <img
            src={logoUrl}
            alt="Uploaded Logo"
            style={{ maxWidth: '100%', height: 'auto', maxHeight: '200px' }}
          />
          <Box>
            <IconButton
              color="error"
              onClick={handleRemove}
              aria-label="Remove logo"
            >
              <DeleteIcon />
            </IconButton>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
            >
              {t('account.logo.replace')}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleUpload}
              />
            </Button>
          </Box>
        </Box>
      ) : (
        <Button
          variant="outlined"
          component="label"
          startIcon={<UploadIcon />}
          color="secondary"
        >
          {t('account.logo.uploadText')}
          <input type="file" accept="image/*" hidden onChange={handleUpload} />
        </Button>
      )}
    </Box>
  );
};
